import { Anchor } from './MarketingShell';
import { Band, DemoCallout, PricingTables, PilotOffer, ServicesList, Boundaries } from './sections';
import { PILOTS, PILOT_CREDIT_DAYS, money } from '@inbrief/shared';

/**
 * The three pages that also exist as sections of the home page.
 *
 * They are not duplicates: the home page carries the short version inside a
 * single argument, and these carry the full one, so that a link in an email or
 * a slide lands somewhere that answers the question on its own. Both render
 * the same components, so the two cannot drift apart.
 */

export function PricingPage() {
  return (
    <>
      <Band
        title="Pricing"
        heading="h1"
        lede="Per practice, with unlimited staff accounts on both plans. All prices exclude VAT."
      >
        <PricingTables heading="h3" />
      </Band>

      <Band title="Pilots and founding customers" tone="tint">
        <PilotOffer />
      </Band>

      <Band title="What happens next">
        <p className="mk-prose">
          There is no self-serve checkout yet. Choosing a plan starts a conversation: we will show
          you the product against a specification you actually hold, agree a pilot or a start date,
          and set the organisation up. A pilot is {money(PILOTS[0].priceGbp)} for a single practice
          over {PILOTS[0].weeks} weeks and is credited against an annual subscription if you convert
          within {PILOT_CREDIT_DAYS} days.
        </p>
        <div className="mk-hero-actions">
          <Anchor to="/contact" className="btn btn-primary">Book a demo</Anchor>
          <Anchor to="/services" className="btn btn-secondary">Optional services</Anchor>
        </div>
      </Band>
    </>
  );
}

export function DemoPage() {
  return (
    <>
      <Band
        title="Try InBrief"
        heading="h1"
        lede="The full interface, with invented practices and contracts. Explore the contract
              breakdown, ask a question, draft a patient letter and translate one."
      >
        <DemoCallout heading="h3" />
      </Band>

      <Band title="Book a demonstration against your own contract" tone="tint">
        <p className="mk-prose">
          The demonstration above uses invented data. The useful version is half an hour with a
          specification your organisation actually holds — we upload it, extract it, and you see
          what it finds and what it misses. That is a more honest test than any example we could
          choose ourselves.
        </p>
        {/*
          This said there was no booking form and to "contact your InBrief
          representative", which was true when the demonstration page was
          written and stopped being true the day the contact page shipped. A
          visitor reading that a company cannot take a booking concludes it is
          not trading — on the page they are most likely to open second.
        */}
        <div className="mk-hero-actions">
          <Anchor to="/contact" className="btn btn-primary">Book a demo</Anchor>
          <Anchor to="/pricing" className="btn btn-secondary">Pilots and pricing</Anchor>
        </div>
        <p className="mk-small">
          Already a customer? <Anchor to="/login">Log in</Anchor>.
        </p>
      </Band>

      <Band title="What it will not do">
        <Boundaries />
      </Band>
    </>
  );
}

export function ServicesPage() {
  return (
    <>
      <Band
        title="InBrief Services"
        heading="h1"
        lede="Optional help from people, for when a contract needs more than reading."
      >
        <ServicesList />
      </Band>

      <Band title="Software and services are separate" tone="tint">
        <Boundaries />
        <p className="mk-small mk-boundary-note">
          The strategies and recommendations these services produce are things people write. Nothing
          in the software generates them, and buying a service does not change what the software
          does. That line is deliberate: it is part of what keeps InBrief outside the medical device
          regime.
        </p>
        <div className="mk-hero-actions">
          <Anchor to="/contact" className="btn btn-primary">Talk to us</Anchor>
          <Anchor to="/pricing" className="btn btn-secondary">See pricing</Anchor>
        </div>
      </Band>
    </>
  );
}
